import React from 'react';
import {
    Combobox as BaseCombobox,
    ComboboxItem,
    ComboboxProps as BaseComboboxProps,
    Dialog,
    DialogSize,
    Tooltip,
} from '@economic/taco';
import useCachedQuery from '../../../hooks/useCachedQuery';
import useQuery from '../../../hooks/useQuery';
import { QueryOptionsApi } from '../../../hooks/QueryOptionsApi';

export interface ComboboxOption extends ComboboxItem {
    data?: any;
    summary?: string;
}

interface ComboboxDialogProps {
    hide: () => void;
    onChange: any;
}

type PrimitiveProps = Omit<BaseComboboxProps, 'dialog' | 'inline' | 'onChange'> & {
    modal: (props: ComboboxDialogProps) => React.ReactNode;
    modalSize?: DialogSize;
    options?: ComboboxOption[];
    onChange?: any;
    value?: any;
    readOnly?: boolean;
    disabled?: boolean;
    valueType?: 'number' | 'string';
    modalDraggable?: boolean;
    autoFixValue?: boolean;
    modalOnOpenAutofocus?: (event: Event) => void;
};

const ComboboxPrimitive = ({
    modal,
    modalSize = 'md',
    onChange,
    options = [],
    invalid,
    value,
    valueType = 'number',
    modalDraggable = false,
    autoFixValue = true,
    modalOnOpenAutofocus,
    ...props
}: PrimitiveProps) => {
    const ref = React.useRef<HTMLInputElement>(null);
    let summary: string | undefined;

    if (value && !invalid) {
        const option = options.find(option => option.value === value || option.value === Number(value));
        summary = option?.summary;
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        let value = event.target.value as unknown;
        if (valueType === 'number') {
            const intValue = parseInt(event.target.value);
            value = Number.isNaN(intValue) ? (autoFixValue ? undefined : event.target.value) : intValue;
        }

        if (onChange) {
            onChange(value);
        }
    };

    const handleExternalChange = (eventOrValue, ...args): void => {
        if (onChange) {
            onChange(eventOrValue, ...args);
        }
        // trigger blur in case of a form validation for e.g. (validation is needed even if value in combobox changes externally)
        if (props.onBlur) {
            props.onBlur(eventOrValue);
        }
    };

    return (
        <>
            <BaseCombobox
                {...props}
                ref={ref}
                dialog={dialogProps => (
                    <Dialog size={modalSize} draggable={modalDraggable} {...dialogProps}>
                        <Dialog.Content aria-label={props['aria-label'] ?? ''} onOpenAutoFocus={modalOnOpenAutofocus}>
                            {({ close }) => modal({ hide: close, onChange: handleExternalChange })}
                        </Dialog.Content>
                    </Dialog>
                )}
                data={options}
                onChange={handleChange}
                invalid={invalid}
                value={value}
            />

            {summary && (
                <Tooltip title={summary}>
                    <span
                        className="text-grey-700 h-4 text-xs text-left leading-normal font-normal truncate -mb-4"
                        style={{ maxWidth: ref.current?.offsetWidth || '100%' }}>
                        {summary}
                    </span>
                </Tooltip>
            )}
        </>
    );
};

// The generic type is defaulting to any because not every combobox usage specifies a type
// TODO: Remove the 'any' once every combobox usage is specifying a type
export interface ComboboxProps<T = any> extends PrimitiveProps {
    api?: QueryOptionsApi;
    endpoint?: string;
    transform?: (data: T[]) => ComboboxOption[];
    transformDependencies?: any[];
    useCache?: boolean;
}

function ComboboxAsync<T>({
    api = QueryOptionsApi.Internal,
    endpoint = '',
    onChange: handleChange,
    options = [],
    transform,
    transformDependencies = [],
    useCache = true,
    valueType = 'number',
    ...props
}: ComboboxProps<T>) {
    const query = useCache ? useCachedQuery(endpoint, options, { api }) : useQuery(endpoint, options, { api });
    const data = api === QueryOptionsApi.Rest ? query.data?.collection || [] : query.data.data || query.data;
    const opts = React.useMemo(() => (transform ? transform([...data]) : data), [JSON.stringify(data), ...transformDependencies]);
    const onChange = async (eventOrValue, data, refresh = false) => {
        let option: ComboboxOption;

        if (eventOrValue === undefined) {
            handleChange(eventOrValue, data);
            return;
        }

        if (eventOrValue.persist) {
            eventOrValue.persist();
            option = opts.find(option => option.value === eventOrValue.target.value);
        } else {
            option = opts.find(option => option.value === eventOrValue);
        }

        if (refresh) {
            await query.refetch();
        }

        handleChange(eventOrValue, option ? option.data : data);
    };

    return <ComboboxPrimitive {...props} valueType={valueType} onChange={onChange} options={opts} />;
}

// The generic type is defaulting to any because not every combobox usage specifies a type
// TODO: Remove the 'any' once every combobox usage is specifying a type
export default function Combobox<T = any>(props: ComboboxProps<T>) {
    if (!props.endpoint) {
        // Removing the props that aren't actually being used in the ComboboxPrimitive component
        const { api, endpoint, transform, transformDependencies, useCache, ...primitiveProps } = props;
        return <ComboboxPrimitive options={props.data} {...primitiveProps} />;
    }

    return <ComboboxAsync {...props} />;
}
